import type { Metadata } from 'next';

import { routing } from '~/lib/navigation';
import { formatPropertyAddress } from '@addava/skills/presentation';

const streetSuffixes: Record<string, string> = {
  AV: 'avenue',
  AVE: 'avenue',
  ST: 'street',
  BLVD: 'boulevard',
  RD: 'road',
  DR: 'drive',
  CT: 'court',
  LN: 'lane',
  PL: 'place',
  TER: 'terrace',
  CIR: 'circle',
  HWY: 'highway',
  ALY: 'alley',
};

export function parcelAddressSlug(address: string) {
  const street = formatPropertyAddress(address)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/,?\s+San Francisco\b.*$/i, '')
    .replace(/^0+(?=\d)/, '')
    .replace(/(-)0+(?=\d)/g, '$1')
    .replace(/\b0+(\d+(?:st|nd|rd|th))\b/gi, '$1')
    .replace(/\./g, '')
    .replace(
      /\b(AV|AVE|ST|BLVD|RD|DR|CT|LN|PL|TER|CIR|HWY|ALY)\b(?=(?:\s+(?:#|UNIT|APT).*)?$)/i,
      (suffix) => streetSuffixes[suffix.toUpperCase()] ?? suffix,
    )
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 160)
    .replace(/^-+|-+$/g, '');
  return street;
}

export function parcelIdentity(block: string, lot: string, address = '') {
  const parcelLabel = `Block ${block}, Lot ${lot}`;
  return {
    block,
    lot,
    parcelLabel,
    address: formatPropertyAddress(address) || parcelLabel,
    slug: parcelAddressSlug(address),
  };
}

export function parcelPreviewPath(block: string, lot: string, locale: string, address = '') {
  const prefix = locale === routing.defaultLocale ? '' : `/${locale}`;
  const path = `${prefix}/demo/san-francisco-ca/${encodeURIComponent(block.trim().toUpperCase())}/${encodeURIComponent(lot.trim().toUpperCase())}`;
  const slug = parcelAddressSlug(address);
  return slug ? `${path}/${slug}` : path;
}

export function parcelShareMetadata(
  identity: ReturnType<typeof parcelIdentity>,
  locale: string,
  siteUrl: string,
  indexable = false,
): Metadata {
  const title = `${identity.address} | AddAva`;
  const description = `San Francisco property records for ${identity.address} (${identity.parcelLabel}): permits, complaints, fire safety, Assessor history, and recorded documents.`;
  const url = new URL(
    parcelPreviewPath(identity.block, identity.lot, locale, identity.slug ? identity.address : ''),
    siteUrl,
  ).href;
  const image = new URL(
    `/api/parcel-watch/share-image?${new URLSearchParams({ block: identity.block, lot: identity.lot })}`,
    siteUrl,
  ).href;
  return {
    title: { absolute: title },
    description,
    alternates: { canonical: url },
    robots: { index: indexable, follow: true },
    openGraph: {
      title,
      description,
      url,
      siteName: 'AddAva',
      type: 'website',
      images: [
        {
          url: image,
          width: 1200,
          height: 630,
          alt: `${identity.address} — San Francisco property records`,
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: [image],
    },
  };
}
